import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "@ag-grid-community/react";
import {ClientSideRowModelModule} from "@ag-grid-community/client-side-row-model";
import {
  ColDef,
  ColumnMovedEvent,
  GetDetailRowDataParams,
  GetRowIdParams,
  GridReadyEvent,
  IDetailCellRendererParams,
  ModuleRegistry,
  RowDragEndEvent
} from "@ag-grid-community/core";
import {MasterDetailModule} from "@ag-grid-enterprise/master-detail";
import styled from "@emotion/styled";
import {getAddressPositionWithCache, useRoutePlanningContext} from "../../providers/RoutePlanningProvider";
import {extractJsonPref, extractSimplePref, usePreferenceContext} from "../../providers/PreferenceProvider";
import {useAppContext} from "../../ApplicationContext";
import {TenantPreferences, UserPreferences} from "../../constants/constants";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../providers/DispatchStationProvider";
import {useDispatchGroupContext} from "../../providers/DispatchGroupProvider";
import {ManifestJobColorPalette} from "../common/cell-renderers/ColoredJobNumberCellRenderer";
import {uniq} from "lodash";
import {AgStop} from "../common/types";
import {ColumnTypes, DefaultColDef} from "./utils/driverColumnConfig";
import {buildMasterColumnDefs} from "./utils/masterColumnDefs";
import {buildStopsColumnDefs} from "./utils/stopsColumnDefs";
import {OtherDriver, RowData} from "./types/types";
import {combineDatesAndTime} from "../../utils/DayjsUtils";
import MoveJobDialog from "./components/MoveJobDialog";
import ChangeDriverDialog from "./components/ChangeDriverDialog";
import {buildSwapChangeOptions} from "./utils/swapChangeOptions";
import {findDriverByVehicleId} from "./utils/RoutePlanning.utils";
import {useOtherDriversPool} from "./hooks/useOtherDriversPool";
import {DriverFilterInput, ModelDriverFilterInput, useSearchVehicleTypesQuery} from "../../generated/graphql";
import {Intent} from "@blueprintjs/core";
import {showToastWithTitle} from "../../utils/toasters/ToasterWithTitle";
/** Display name for a driver-ish row/record (DP-3039 success toasts). */
const driverDisplayName = (driver: {name?: string | null; driverCode?: string | null; driverId?: number | null}) =>
  driver.name ?? driver.driverCode ?? `Driver ${driver.driverId}`;

/** Resequence 1..n and clear ETAs — a route's stops after its driver changed (DP-3039). */
const resequenceAndClearEtas = (stops: AgStop[]): AgStop[] =>
  stops.map(
    (s, i) =>
      ({
        ...s,
        manifestSequence: i + 1,
        eta: undefined,
        arrivalTime: undefined,
        departureTime: undefined,
        etaSource: undefined,
        generatedDateTime: undefined
      } as AgStop)
  );

/**
 * DP-3039: the driver side of a route row — identity + Step 2 (start/end location,
 * date/time, lat/lng). This is the exact field set handleChangeDriver replaces; on
 * Swap it travels between the two rows while each route keeps its stops.
 */
const extractDriverSide = (row: RowData) => ({
  driverId: row.driverId,
  manifestDriverId: row.manifestDriverId,
  name: row.name,
  driverCode: row.driverCode,
  vehicleDescription: row.vehicleDescription,
  startDate: row.startDate,
  startTime: row.startTime,
  startTimeZone: row.startTimeZone,
  startDateTime: row.startDateTime,
  startLocation: row.startLocation,
  endLocation: row.endLocation,
  startLat: row.startLat,
  startLng: row.startLng,
  endLat: row.endLat,
  endLng: row.endLng
});

ModuleRegistry.registerModules([ClientSideRowModelModule, MasterDetailModule]);

type ColumnOrderItem = {colId: string; pinned: boolean};

type Props = {
  onToggleRoute: (routeId: string) => void;
  hiddenRouteIds: string[];
  workingRowData: RowData[] | null;
  setWorkingRowData: React.Dispatch<React.SetStateAction<RowData[] | null>>;
  setUnassignedJobIds: React.Dispatch<React.SetStateAction<number[]>>;
  hiddenColumnIds: Set<string>;
  columnOrder: ColumnOrderItem[] | null;
  onColumnOrderChange: (columns: ColumnOrderItem[]) => void;
};

const DriverResultList = ({
  onToggleRoute,
  hiddenRouteIds,
  workingRowData,
  setWorkingRowData,
  setUnassignedJobIds,
  hiddenColumnIds,
  columnOrder,
  onColumnOrderChange
}: Props) => {
  const {etaFromOptimization: etaFromOptimizationEnabled, skills: skillsEnabled} = useFeatureFlagContext();
  const {tenantPreferences, userPreferences} = usePreferenceContext();
  const {selectedDrivers, allowMovingExistingWork, manifestStopsMap} = useRoutePlanningContext();
  const {skills} = useDispatchDataContext();
  const {appState} = useAppContext();
  const dispatchStationState = useDispatchStationContext();
  const {driverGroups} = useDispatchGroupContext();
  const isTimezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false)
    .value as boolean;

  // Ref for workingRowData so cell renderers always read the latest value on interaction.
  // Declared before columnDefs — it is passed into buildMasterColumnDefs during render.
  const workingRowDataRef = useRef<RowData[]>(workingRowData ?? []);
  workingRowDataRef.current = workingRowData ?? [];

  // DP-3202: Derive which manifests are still untouched by comparing original
  // stops with the current working row data. A manifest is untouched when its
  // stops match the original sequence exactly and no new jobs were added.
  // Reactive — recomputes whenever workingRowData changes.
  const untouchedManifestIds = useMemo(() => {
    if (!workingRowData) return undefined;
    const manifestDriverIds = Object.keys(manifestStopsMap).map(Number);
    if (manifestDriverIds.length === 0) return undefined;
    const untouchedMdIds = manifestDriverIds.filter((mdId) => {
      const originalStopIds = new Set((manifestStopsMap[mdId] ?? []).map((s) => s.jobStopId));
      const originalSequence = (manifestStopsMap[mdId] ?? [])
        .sort((a, b) => a.manifestSequence - b.manifestSequence)
        .map((s) => s.jobStopId);
      // DP-3175: A driver may appear on multiple routes. Collect stops from ALL
      // rows for this manifest to compare against the original sequence.
      const rows = workingRowData.filter((r) => r.manifestDriverId === mdId);
      if (rows.length === 0) return false;
      const currentStops = rows.flatMap((r) => r.assignedStops);
      // If any stop is NOT from the original manifest → touched
      if (currentStops.some((s) => !originalStopIds.has(s.jobStopId))) return false;
      // Compare full sequence (including completed stops)
      const currentSequence = currentStops.map((s) => s.jobStopId);
      return (
        currentSequence.length === originalSequence.length &&
        currentSequence.every((id, i) => id === originalSequence[i])
      );
    });
    return untouchedMdIds.length > 0 ? untouchedMdIds : undefined;
  }, [manifestStopsMap, workingRowData]);

  // DP-3039: "Other drivers" pool for the Change scope. DriversGrid (Step 1) loads drivers
  // through a server-side GraphQL datasource — no React context holds a driver list — so the
  // pool is fetched here, mirroring the Step-1 scope: active drivers, station-scoped dispatch
  // groups when stations are selected.
  // Scope the Other-drivers pool to the navbar DISPATCH STATION only, kept reactive: the pool =
  // active drivers in the currently-selected station's dispatch groups (all active drivers when
  // "All Stations"). It intentionally does NOT apply the Step-1 driver-group filter
  // (`manifestGroupHeader` pref) — only the station follows the navbar. Search keyword is a
  // separate dropdown-search concern and is not included here.
  const otherDriversFilter = useMemo((): DriverFilterInput => {
    const conditions: ModelDriverFilterInput[] = [{driverStatus: {eq: "A"}}];
    // Guard against an empty `or` (station selected but its groups haven't loaded yet, or a
    // group-less station) — an empty OR is ambiguous on the backend.
    if (hasSelectedDispatchStations(dispatchStationState) && driverGroups.length > 0) {
      conditions.push({or: driverGroups.map((group) => ({dispatchGroupId: {eq: group.dispatchGroupId}}))});
    }
    return {driver: {and: conditions}};
  }, [dispatchStationState, driverGroups]);

  // Paginated + searchable pool (infinite scroll). The scope filter above stays reactive; the
  // hook adds one-page-at-a-time fetching plus server-side search on name/driverCode (same as
  // the Step-1 grid). Search keyword is a dropdown concern, layered on top of the scope filter.
  const {
    drivers: otherDriversRaw,
    loading: otherDriversLoading,
    hasMore: otherDriversHasMore,
    onSearch: onOtherDriversSearch,
    loadMore: onOtherDriversLoadMore
  } = useOtherDriversPool({filter: otherDriversFilter});
  const vehicleTypesResult = useSearchVehicleTypesQuery({variables: {limit: 1000}});

  // Active drivers not in the run; vehicle description resolved the same way DriversGrid
  // does on ADD_DRIVER (vehicleTypeId → searchVehicleTypes description).
  const otherDriversPool = useMemo((): OtherDriver[] => {
    const vehicleTypes = vehicleTypesResult.data?.searchVehicleTypes?.items ?? [];
    return otherDriversRaw
      .filter((driver) => !selectedDrivers.some((selected) => selected.driverId === driver.driverId))
      .map((driver) => ({
        driverId: driver.driverId,
        name: driver.name,
        driverCode: driver.driverCode,
        vehicleDescription:
          vehicleTypes.find((vehicle) => vehicle?.vehicleTypeId === driver.vehicleTypeId)?.description ?? null
      }));
  }, [otherDriversRaw, vehicleTypesResult.data, selectedDrivers]);

  // Accumulate every Other driver ever fetched (across pages + searches). The live pool resets on
  // search-clear, but a picked driver must still resolve at Save time — this map never shrinks.
  const seenOtherDriversRef = useRef(new Map<number, OtherDriver>());
  useEffect(() => {
    otherDriversPool.forEach((driver) => seenOtherDriversRef.current.set(driver.driverId, driver));
  }, [otherDriversPool]);

  const buildJobColoringIndex = useCallback((stops: AgStop[]) => {
    const coloringIndex = new Map<string, number>();
    const jobNumbers = uniq(stops.map((s) => s.job?.jobNumber).filter(Boolean)) as string[];
    if (jobNumbers.length <= ManifestJobColorPalette.length) {
      jobNumbers.forEach((value, index) => coloringIndex.set(value, index));
    }
    return coloringIndex;
  }, []);

  // State for the Move Job dialog (lifted out of cell renderer so it reads fresh workingRowData)
  const [moveDialogState, setMoveDialogState] = useState<{
    stop: AgStop;
    sourceDriverId: number;
    sourceRouteId: string;
  } | null>(null);

  const handleMoveJobClick = useCallback((stop: AgStop, parentDriverId: number, parentRouteId: string) => {
    setMoveDialogState({stop, sourceDriverId: parentDriverId, sourceRouteId: parentRouteId});
  }, []);

  // Move job from one route to another
  const handleMoveJob = useCallback(
    (jobId: number, sourceRouteId: string, targetRouteId: string) => {
      setWorkingRowData((prev) => {
        if (!prev) return prev;
        const rows = [...prev];
        const sourceIdx = rows.findIndex((r) => r.routeId === sourceRouteId);
        const targetIdx = rows.findIndex((r) => r.routeId === targetRouteId);
        if (sourceIdx === -1 || targetIdx === -1) return prev;

        const source = {...rows[sourceIdx]};
        const target = {...rows[targetIdx]};

        const stopsToMove = source.assignedStops.filter((s) => s.job?.jobId === jobId);
        source.assignedStops = source.assignedStops
          .filter((s) => s.job?.jobId !== jobId)
          .map(
            (s, i) =>
              ({
                ...s,
                manifestSequence: i + 1,
                eta: undefined,
                arrivalTime: undefined,
                departureTime: undefined,
                etaSource: undefined,
                generatedDateTime: undefined
              } as AgStop)
          );
        target.assignedStops = [...target.assignedStops, ...stopsToMove].map(
          (s, i) =>
            ({
              ...s,
              manifestSequence: i + 1,
              eta: undefined,
              arrivalTime: undefined,
              departureTime: undefined,
              etaSource: undefined,
              generatedDateTime: undefined
            } as AgStop)
        );

        source.endTime = undefined;
        source.duration = 0;
        source.miles = 0;
        target.endTime = undefined;
        target.duration = 0;
        target.miles = 0;

        rows[sourceIdx] = source;
        rows[targetIdx] = target;
        return rows;
      });
    },
    [setWorkingRowData]
  );

  // Delete job from a driver
  const handleDeleteJob = useCallback(
    (jobId: number, driverRowIndex: number) => {
      setWorkingRowData((prev) => {
        if (!prev || driverRowIndex < 0 || driverRowIndex >= prev.length) return prev;
        const rows = [...prev];
        const driver = {...rows[driverRowIndex]};

        driver.assignedStops = driver.assignedStops
          .filter((s) => s.job?.jobId !== jobId)
          .map(
            (s, i) =>
              ({
                ...s,
                manifestSequence: i + 1,
                eta: undefined,
                arrivalTime: undefined,
                departureTime: undefined,
                etaSource: undefined,
                generatedDateTime: undefined
              } as AgStop)
          );
        driver.endTime = undefined;
        driver.duration = 0;
        driver.miles = 0;

        rows[driverRowIndex] = driver;
        return rows;
      });
      setUnassignedJobIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
    },
    [setWorkingRowData, setUnassignedJobIds]
  );

  // Change driver: replace the driver info on a route (keeps the same stops, clears ETAs)
  const handleChangeDriver = useCallback(
    async (sourceRouteId: string, targetVehicleId: string) => {
      // Replaced driver's name (from fresh rows) — for the success toast.
      const sourceRow = workingRowDataRef.current.find((r) => r.routeId === sourceRouteId);
      const oldDriverName = sourceRow ? driverDisplayName(sourceRow) : "";
      const targetDriver = findDriverByVehicleId(targetVehicleId, selectedDrivers);
      if (!targetDriver) {
        // DP-3039: "Other drivers" — the target is outside the run and has no Step 2 config,
        // so the route keeps the replaced driver's Step 2 (start/end location, date/time,
        // lat/lng, startDateTime) and only the driver identity changes (PO-confirmed:
        // "same step 2 information"). No geocode / startDateTime recompute.
        // Look up the pool first, then the accumulated "seen" map: the pool is server-searched and
        // resets to page 0 when the dropdown search clears, so a driver picked from a later page or
        // a search result may no longer be in `otherDriversPool` by the time Save fires.
        const otherTargetId = Number(targetVehicleId.split(":")[1]);
        const otherDriver =
          otherDriversPool.find((d) => d.driverId === otherTargetId) ?? seenOtherDriversRef.current.get(otherTargetId);
        if (!otherDriver) return;
        setWorkingRowData((prev) => {
          if (!prev) return prev;
          const rows = [...prev];
          const sourceIdx = rows.findIndex((r) => r.routeId === sourceRouteId);
          if (sourceIdx === -1) return prev;
          rows[sourceIdx] = {
            ...rows[sourceIdx],
            driverId: otherDriver.driverId,
            manifestDriverId: undefined,
            name: otherDriver.name,
            driverCode: otherDriver.driverCode,
            vehicleDescription: otherDriver.vehicleDescription,
            // Clear ETAs since a different driver runs the route now
            assignedStops: resequenceAndClearEtas(rows[sourceIdx].assignedStops),
            endTime: undefined,
            duration: 0,
            miles: 0
          };
          return rows;
        });
        showToastWithTitle({
          intent: Intent.SUCCESS,
          title: "Success",
          message: `${oldDriverName}'s route has been changed to ${driverDisplayName(otherDriver)}.`
        });
        return;
      }

      // Geocode the new driver's start address to get lat/lng for the map
      let startLat: number | undefined;
      let startLng: number | undefined;
      if (targetDriver.startLocation) {
        const position = await getAddressPositionWithCache(targetDriver.startLocation);
        if (position) {
          startLat = position.latitude;
          startLng = position.longitude;
        }
      }
      // Fall back to existing route of the same driver if geocoding failed
      if (startLat == null || startLng == null) {
        const existingRow = workingRowDataRef.current.find(
          (r) => r.driverId === targetDriver.driverId && r.startLat != null
        );
        if (existingRow) {
          startLat = existingRow.startLat;
          startLng = existingRow.startLng;
        }
      }

      // Geocode end location for accurate distance/polyline
      let endLat: number | undefined;
      let endLng: number | undefined;
      if (targetDriver.endLocation) {
        const endPosition = await getAddressPositionWithCache(targetDriver.endLocation);
        if (endPosition) {
          endLat = endPosition.latitude;
          endLng = endPosition.longitude;
        }
      }

      // The new driver has its own start datetime — derive the ISO base used for ETA recalc
      // (matches how buildRowData sets startDateTime), else recalc keeps the old driver's time.
      const combinedStart = combineDatesAndTime(
        targetDriver.startDate,
        targetDriver.startTime,
        targetDriver.startTimeZone
      );
      const startDateTime = combinedStart.isValid() ? combinedStart.toISOString() : undefined;

      setWorkingRowData((prev) => {
        if (!prev) return prev;
        const rows = [...prev];
        const sourceIdx = rows.findIndex((r) => r.routeId === sourceRouteId);
        if (sourceIdx === -1) return prev;

        const updated = {
          ...rows[sourceIdx],
          // Replace driver identity fields from the selected driver.
          // DP-3175: Do NOT copy manifestDriverId — changing a new route's driver
          // to a driver with an existing manifest does not make this route a manifest route.
          driverId: targetDriver.driverId,
          manifestDriverId: rows[sourceIdx].manifestDriverId,
          name: targetDriver.name,
          driverCode: targetDriver.driverCode,
          vehicleDescription: targetDriver.vehicleDescription,
          startDate: targetDriver.startDate,
          startTime: targetDriver.startTime,
          startTimeZone: targetDriver.startTimeZone,
          startDateTime,
          startLocation: targetDriver.startLocation,
          endLocation: targetDriver.endLocation,
          startLat,
          startLng,
          endLat,
          endLng,
          // Clear ETAs since the new driver has a different location
          assignedStops: rows[sourceIdx].assignedStops.map(
            (s, i) =>
              ({
                ...s,
                manifestSequence: i + 1,
                eta: undefined,
                arrivalTime: undefined,
                departureTime: undefined,
                etaSource: undefined,
                generatedDateTime: undefined
              } as AgStop)
          ),
          endTime: undefined,
          duration: 0,
          miles: 0
        };

        rows[sourceIdx] = updated;
        return rows;
      });

      showToastWithTitle({
        intent: Intent.SUCCESS,
        title: "Success",
        message: `${oldDriverName}'s route has been changed to ${driverDisplayName(targetDriver)}.`
      });
    },
    [setWorkingRowData, selectedDrivers, otherDriversPool]
  );

  // DP-3039: Swap = Change applied both ways — one atomic exchange of the driver side
  // (identity + Step 2) across both route rows. Each route keeps its stops; ETAs clear
  // and stops resequence on BOTH routes. No geocode needed: both rows already carry
  // their driver's lat/lng and startDateTime, which travel with the driver.
  const handleSwapDriver = useCallback(
    (sourceRouteId: string, targetRouteId: string) => {
      // Resolve the two drivers from the fresh rows up front (for the success toast + guard).
      const currentRows = workingRowDataRef.current;
      const source = currentRows.find((r) => r.routeId === sourceRouteId);
      const target = currentRows.find((r) => r.routeId === targetRouteId);
      if (!source || !target || sourceRouteId === targetRouteId) return;

      setWorkingRowData((prev) => {
        if (!prev) return prev;
        const sourceIdx = prev.findIndex((r) => r.routeId === sourceRouteId);
        const targetIdx = prev.findIndex((r) => r.routeId === targetRouteId);
        if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return prev;

        const rows = [...prev];
        const sourceRow = rows[sourceIdx];
        const targetRow = rows[targetIdx];

        rows[sourceIdx] = {
          ...sourceRow,
          ...extractDriverSide(targetRow),
          assignedStops: resequenceAndClearEtas(sourceRow.assignedStops),
          endTime: undefined,
          duration: 0,
          miles: 0
        };
        rows[targetIdx] = {
          ...targetRow,
          ...extractDriverSide(sourceRow),
          assignedStops: resequenceAndClearEtas(targetRow.assignedStops),
          endTime: undefined,
          duration: 0,
          miles: 0
        };
        return rows;
      });

      // Success toast (design order: the swapped-with driver, then the acted-on route's driver).
      showToastWithTitle({
        intent: Intent.SUCCESS,
        title: "Success",
        message: `${driverDisplayName(target)} and ${driverDisplayName(source)}'s routes have been swapped.`
      });
    },
    [setWorkingRowData]
  );

  // Delete route: remove route by routeId and mark jobs as unassigned
  const handleDeleteRoute = useCallback(
    (routeId: string) => {
      setWorkingRowData((prev) => {
        if (!prev) return prev;
        const route = prev.find((r) => r.routeId === routeId);
        if (!route) return prev;

        // Collect unique job IDs from the route's stops
        const jobIds = uniq(route.assignedStops.map((s) => s.job?.jobId).filter(Boolean)) as number[];
        setUnassignedJobIds((prevIds) => [...prevIds, ...jobIds.filter((id) => !prevIds.includes(id))]);

        return prev.filter((r) => r.routeId !== routeId);
      });
    },
    [setWorkingRowData, setUnassignedJobIds]
  );

  // Sync grid row visibility with hiddenRouteIds
  useEffect(() => {
    setWorkingRowData(
      (prev) => prev?.map((row) => ({...row, isVisible: !hiddenRouteIds.includes(row.routeId)})) ?? null
    );
  }, [hiddenRouteIds, setWorkingRowData]);

  // DP-3039: the Swap-or-Change dialog is lifted out of the driver cell (same as MoveJobDialog) so
  // it survives grid re-renders — infinite-scroll page fetches mutate the Other-drivers pool, which
  // would otherwise rebuild columnDefs, recreate the cell renderer, and close a dialog living inside
  // it. Only the acted-on route id lives in the cell; all option lists are computed here from fresh
  // working rows.
  const [swapChangeRouteId, setSwapChangeRouteId] = useState<string | null>(null);
  const handleOpenSwapChange = useCallback((routeId: string) => setSwapChangeRouteId(routeId), []);
  const hasOtherDrivers = otherDriversPool.length > 0;
  const swapChangeOptions = useMemo(
    () =>
      swapChangeRouteId != null
        ? buildSwapChangeOptions(swapChangeRouteId, workingRowData ?? [], selectedDrivers, otherDriversPool)
        : null,
    [swapChangeRouteId, workingRowData, selectedDrivers, otherDriversPool]
  );

  // Master grid column defs
  const columnDefs = useMemo(
    (): ColDef<RowData>[] =>
      buildMasterColumnDefs({
        appState,
        isTimezoneOfJobStop,
        tenantPreferences,
        onToggleRoute,
        setWorkingRowData,
        selectedDrivers,
        hasOtherDrivers,
        workingRowDataRef,
        onOpenSwapChange: handleOpenSwapChange,
        onDeleteRoute: handleDeleteRoute,
        showManifestStar: !allowMovingExistingWork && Object.keys(manifestStopsMap).length > 0,
        untouchedManifestDriverIds: untouchedManifestIds
      }),
    [
      appState,
      isTimezoneOfJobStop,
      onToggleRoute,
      tenantPreferences,
      setWorkingRowData,
      selectedDrivers,
      hasOtherDrivers,
      handleOpenSwapChange,
      handleDeleteRoute,
      allowMovingExistingWork,
      manifestStopsMap,
      untouchedManifestIds
    ]
  );

  // Build stops column defs for a specific parent route
  const buildStopsColumnDefsForDriver = useCallback(
    (parentDriverId: number, parentRouteId: string): ColDef<AgStop>[] => {
      const baseDefs = buildStopsColumnDefs({
        appState,
        isTimezoneOfJobStop,
        etaFromOptimizationEnabled,
        skillsEnabled,
        skills,
        buildJobColoringIndex,
        workingRowDataRef,
        onMoveJobClick: handleMoveJobClick,
        onDeleteJob: handleDeleteJob,
        parentDriverId,
        parentRouteId
      });

      // Apply column visibility and order from manifest detail grid user preferences
      const columnState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridColumnState)
        ?.value as ColDef[];

      // Build a map of colId → colDef with visibility applied
      const pinnedDefs = baseDefs.filter((cd) => cd.pinned);
      const pinnedColIds = new Set(pinnedDefs.map((cd) => cd.colId ?? cd.field));
      const colDefMap = new Map<string, ColDef<AgStop>>();

      baseDefs.forEach((cd) => {
        const colId = (cd.colId ?? cd.field) as string;
        if (colId && !pinnedColIds.has(colId)) {
          colDefMap.set(colId, {...cd, hide: hiddenColumnIds.has(colId)});
        }
      });

      // Apply manifest detail preferences for column order (if no custom columnOrder)
      let orderedNonPinned: ColDef<AgStop>[];
      if (columnOrder) {
        // Use column chooser order
        orderedNonPinned = columnOrder
          .filter((item) => !item.pinned && colDefMap.has(item.colId))
          .map((item) => colDefMap.get(item.colId)!);
        // Append any columns not in columnOrder
        colDefMap.forEach((cd, colId) => {
          if (!columnOrder.some((item) => item.colId === colId)) orderedNonPinned.push(cd);
        });
      } else if (columnState && Array.isArray(columnState)) {
        // Use manifest detail preferences for order
        orderedNonPinned = [];
        const matched = new Set<string>();
        columnState.forEach((savedCol: any) => {
          if (pinnedColIds.has(savedCol.colId)) return;
          const cd = colDefMap.get(savedCol.colId);
          if (cd) {
            matched.add(savedCol.colId);
            orderedNonPinned.push(cd);
          }
        });
        colDefMap.forEach((cd, colId) => {
          if (!matched.has(colId)) orderedNonPinned.push(cd);
        });
      } else {
        // No preferences, no custom order — use base order
        orderedNonPinned = Array.from(colDefMap.values());
      }

      return [...pinnedDefs, ...orderedNonPinned];
    },
    [
      appState,
      isTimezoneOfJobStop,
      etaFromOptimizationEnabled,
      skillsEnabled,
      skills,
      buildJobColoringIndex,
      handleMoveJob,
      handleDeleteJob,
      userPreferences,
      hiddenColumnIds,
      columnOrder
    ]
  );

  // Drag-and-drop: commit the final order to workingRowData on drag end
  const handleDetailRowDragEnd = useCallback(
    (event: RowDragEndEvent<AgStop>) => {
      if (!event.node?.data) return;
      const draggedStop = event.node.data;

      const currentStops: AgStop[] = [];
      event.api.forEachNode((node) => {
        if (node.data) {
          currentStops.push(node.data);
        }
      });

      // Find the driver that owns these stops
      setWorkingRowData((prev) => {
        if (!prev) return prev;
        const driverIndex = prev.findIndex((row) =>
          row.assignedStops.some((s) => s.jobStopId === draggedStop.jobStopId)
        );
        if (driverIndex === -1) return prev;

        const driver = prev[driverIndex];

        // Skip update if order hasn't changed
        const originalOrder = driver.assignedStops.map((s) => s.jobStopId);
        const newOrder = currentStops.map((s) => s.jobStopId);
        if (originalOrder.length === newOrder.length && originalOrder.every((id, i) => id === newOrder[i])) {
          return prev;
        }

        const updatedRows = [...prev];
        const updatedDriver = {...driver};

        // Renumber manifestSequence and clear ETAs
        updatedDriver.assignedStops = currentStops.map((stop, i) => ({
          ...stop,
          manifestSequence: i + 1,
          eta: undefined,
          arrivalTime: undefined,
          departureTime: undefined,
          etaSource: undefined,
          generatedDateTime: undefined
        })) as AgStop[];
        updatedDriver.endTime = undefined;
        updatedDriver.duration = 0;
        updatedDriver.miles = 0;

        updatedRows[driverIndex] = updatedDriver;

        // DP-2996: Enable pickup-before-delivery validation warning after full QA on stop type edge cases (BOTH stops, multi-stop jobs)
        // const wrongOrderJobs = getWrongOrderJobNumbers(updatedDriver.assignedStops);
        // if (wrongOrderJobs.length > 0) {
        //   AppToasterTopLeft.show({
        //     message: `Warning: Pickup should come before delivery for job(s): ${wrongOrderJobs.join(", ")}`,
        //     intent: Intent.WARNING,
        //     timeout: 5000
        //   });
        // }

        return updatedRows;
      });
    },
    [setWorkingRowData]
  );

  // Sync column order from grid back to chooser when user drags columns in the detail grid
  const handleDetailColumnMoved = useCallback(
    (event: ColumnMovedEvent<AgStop>) => {
      if (!event.finished || event.source !== "uiColumnMoved") return;
      const allCols = event.columnApi.getAllGridColumns();
      if (!allCols?.length) return;
      const newOrder: ColumnOrderItem[] = allCols
        .filter((col) => {
          const colId = col.getColId();
          return colId !== "dragColumn" && colId !== "actionsColumn";
        })
        .map((col) => ({
          colId: col.getColId(),
          pinned: !!col.isPinned()
        }));
      onColumnOrderChange(newOrder);
    },
    [onColumnOrderChange]
  );

  const detailCellRendererParams = useCallback(
    (masterRowParams: any) => {
      const parentDriverId = masterRowParams.data?.driverId;
      const parentRouteId = masterRowParams.data?.routeId;
      return {
        detailGridOptions: {
          columnDefs: buildStopsColumnDefsForDriver(parentDriverId, parentRouteId),
          defaultColDef: DefaultColDef,
          onFirstDataRendered: (params: any) => {
            const skipCols = new Set(["dragColumn", "actionsColumn"]);
            const cols = params.columnApi.getAllColumns()?.filter((c: any) => !skipCols.has(c.getColId()));
            if (cols?.length) params.columnApi.autoSizeColumns(cols);
          },
          onGridReady: (params: any) => {
            const eBodyViewport: HTMLElement | undefined = params.api.gridBodyCtrl?.eBodyViewport;
            if (!eBodyViewport) return;
            const detailRow = eBodyViewport.closest<HTMLElement>(".ag-details-row");
            const centerColsViewport = eBodyViewport.querySelector<HTMLElement>(".ag-center-cols-viewport");
            if (!detailRow || !centerColsViewport) return;
            const controller = new AbortController();
            detailRow.addEventListener(
              "wheel",
              (e: WheelEvent) => {
                if (e.deltaX && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                  e.stopPropagation();
                  centerColsViewport.scrollBy({left: e.deltaX});
                  e.preventDefault();
                }
              },
              {passive: false, signal: controller.signal}
            );
            params.api.addEventListener("gridPreDestroyed", () => controller.abort());
          },
          getRowId: (params: {data: AgStop}) => String(params.data.jobStopId),
          rowDragManaged: true,
          animateRows: true,
          onRowDragEnd: handleDetailRowDragEnd,
          onColumnMoved: handleDetailColumnMoved,
          suppressDragLeaveHidesColumns: true,
          suppressCellFocus: true,
          suppressContextMenu: true
        },
        getDetailRowData: (params: GetDetailRowDataParams<RowData, AgStop>) => {
          params.successCallback(params.data.assignedStops);
        }
      } as unknown as IDetailCellRendererParams<RowData>;
    },
    [buildStopsColumnDefsForDriver, handleDetailRowDragEnd, handleDetailColumnMoved]
  );

  const getRowId = useCallback((params: GetRowIdParams<RowData>) => params.data.routeId, []);

  const gridRef = useRef<AgGridReact<RowData>>(null);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.columnApi.autoSizeAllColumns();
  }, []);

  // Update detail grid column defs when column visibility/order changes
  useEffect(() => {
    const api = gridRef.current?.api;
    const rows = workingRowDataRef.current;
    if (!api || !rows.length) return;

    rows.forEach((row) => {
      const detailGridInfo = api.getDetailGridInfo(`detail_${row.routeId}`);
      if (detailGridInfo?.api) {
        const newColDefs = buildStopsColumnDefsForDriver(row.driverId as number, row.routeId);
        detailGridInfo.api.setColumnDefs(newColDefs);
      }
    });
  }, [buildStopsColumnDefsForDriver]);

  // Refresh only the detail grids whose assignedStops actually changed
  const prevWorkingRowDataRef = useRef<RowData[] | null>(null);
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !workingRowData) return;

    const prev = prevWorkingRowDataRef.current;
    prevWorkingRowDataRef.current = workingRowData;

    workingRowData.forEach((row) => {
      const prevRow = prev?.find((r) => r.routeId === row.routeId);
      if (prevRow?.assignedStops === row.assignedStops) return;

      const detailGridInfo = api.getDetailGridInfo(`detail_${row.routeId}`);
      if (detailGridInfo?.api) {
        detailGridInfo.api.setRowData(row.assignedStops);
      }
    });
  }, [workingRowData]);

  return (
    <GridContainer className="ag-theme-alpine">
      <AgGridReact<RowData>
        ref={gridRef}
        className="drivers-results-grid"
        defaultColDef={DefaultColDef}
        columnDefs={columnDefs}
        rowData={workingRowData}
        suppressDragLeaveHidesColumns
        suppressCellFocus
        suppressContextMenu
        enableCellTextSelection
        masterDetail
        getRowId={getRowId}
        detailCellRendererParams={detailCellRendererParams}
        onGridReady={onGridReady}
        columnTypes={ColumnTypes}
      />
      <MoveJobDialog
        isOpen={moveDialogState !== null}
        onClose={() => setMoveDialogState(null)}
        stop={moveDialogState?.stop ?? null}
        sourceDriverId={moveDialogState?.sourceDriverId ?? 0}
        sourceRouteId={moveDialogState?.sourceRouteId ?? ""}
        workingRowData={workingRowData ?? []}
        onMoveJob={(jobId, sourceRouteId, targetRouteId) => {
          handleMoveJob(jobId, sourceRouteId, targetRouteId);
          setMoveDialogState(null);
        }}
      />
      <ChangeDriverDialog
        isOpen={swapChangeRouteId !== null}
        onClose={() => setSwapChangeRouteId(null)}
        currentDriverName={swapChangeOptions?.currentDriverName ?? ""}
        currentRouteLabel={swapChangeOptions?.currentRouteLabel ?? ""}
        swapOptions={swapChangeOptions?.swapOptions ?? []}
        currentlySelectedOptions={swapChangeOptions?.currentlySelectedOptions ?? []}
        otherDriverOptions={swapChangeOptions?.otherDriverOptions ?? []}
        otherDriversLoading={otherDriversLoading}
        otherDriversHasMore={otherDriversHasMore}
        onOtherDriversSearch={onOtherDriversSearch}
        onOtherDriversLoadMore={onOtherDriversLoadMore}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onChange={(targetVehicleId) =>
          swapChangeRouteId !== null ? handleChangeDriver(swapChangeRouteId, targetVehicleId) : undefined
        }
        onSwap={(targetRouteId) => {
          if (swapChangeRouteId !== null) handleSwapDriver(swapChangeRouteId, targetRouteId);
        }}
      />
    </GridContainer>
  );
};

export default DriverResultList;

const GridContainer = styled.div`
  flex-grow: 1;
  font-family: "Roboto", sans-serif;
  font-size: 14px;

  .uppercase-header {
    text-transform: uppercase;
  }

  .ag-cell[col-id="eta"] {
    .bp5-popover-target {
      display: block;
      height: 100%;

      > div {
        height: 100%;
      }
    }
  }

  .ag-details-row .ag-cell[col-id="dragColumn"],
  .ag-details-row .ag-cell[col-id="actionsColumn"] {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* DP-3175 B08: ag-Grid's drag handle is invisible but still receives pointer
     events — it overlays the custom DragCellRenderer icon which provides the visual. */
  .ag-details-row .ag-cell[col-id="dragColumn"] .ag-row-drag {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: grab;
  }

  .ag-details-row .ag-header-cell[col-id="manifestSequence"] .ag-header-cell-label {
    justify-content: center;
  }

  .ag-details-row .ag-cell[col-id="manifestSequence"] {
    display: flex;
    justify-content: center;
  }

  .ag-details-row .ag-cell[col-id="job.jobNumber"] > div {
    height: 100%;
  }

  .ag-details-row .ag-cell[col-id="stopType"] {
    display: flex;
    align-items: center;
  }

  .ag-row-dragging {
    user-select: none;
  }
`;
